import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetUserDetailQuery } from "../../service/features/api";
import { GENDER, VEHICLE_TYPE } from "../../constants/enum";
import { getVehicleTypeLabel } from "../../utils/vehicle";
import {
  Box,
  Tab,
  Tabs,
} from "@mui/material";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import VehicleListings from "../../features/manageVehicles/vehicleListings";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = ({ children, value, index }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && children}
  </div>
);

const a11yProps = (index: number) => ({
  id: `tab-${index}`,
  "aria-controls": `tabpanel-${index}`,
});

const tabTypes = [
  VEHICLE_TYPE.CAR,
  VEHICLE_TYPE.BIKE,
  VEHICLE_TYPE.BICYCLE,
  VEHICLE_TYPE.CARAVAN,
  VEHICLE_TYPE.TRUCK,
  VEHICLE_TYPE.HEAVY_VEHICLE
].map(type => getVehicleTypeLabel(type));

const ManageUserDetail = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [valueTabs, setValueTabs] = React.useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const { id } = useParams();
  const { data: userDetail } = useGetUserDetailQuery(id, { skip: !id });
  const userData = userDetail?.data || {};

  const profileInfo = [
    { label: "الاسم الكامل / Full Name", value: userData.fullName || "N/A" },
    { label: "البريد الإلكتروني / Email", value: userData.email || "N/A" },
    { label: "رقم الهاتف / Phone", value: userData.phone ? `${userData.countryCode || ""} ${userData.phone}` : "N/A" },
    {
      label: "الجنس / Gender",
      value: userData.gender
        ? Object.keys(GENDER).find(key => GENDER[key as keyof typeof GENDER] === userData.gender) || "N/A"
        : "N/A"
    },
    { label: "البلد / Country", value: userData.address?.country || userData.location?.country || "N/A" },
    { label: "الولاية / State", value: userData.address?.state || userData.location?.state || "N/A" },
    { label: "المدينة / City", value: userData.address?.city || userData.location?.city || "N/A" },
    { label: "تاريخ التسجيل / Date Registered", value: userData.createdAt ? new Date(userData.createdAt).toLocaleDateString() : "N/A" },
  ];


  return (
    <>
      {/* Page Title */}
      <CommonBreadcrumb
        title="تفاصيل العميل / Customer Detail"
        breadcrumbItems={[
          { label: "لوحة المعلومات / Dashboard", onClick: () => navigate("/dashboard") },
          { label: "إدارة العملاء / Customer Management", onClick: () => navigate("/manage-users") },
          { label: "تفاصيل العميل / Customer Detail" },
        ]}
      />

      {/* Tabs */}
      <div className="table_header">
        <Box className="custom_tabs1">
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            className="custom_tabs_links"
          >
            <Tab label="معلومات الملف الشخصي / Profile Information" {...a11yProps(0)} />
            <Tab label="المركبات المنشورة / Posted Vehicles" {...a11yProps(1)} />
            <Tab label="المركبات المحفوظة / Saved Vehicles" {...a11yProps(2)} />
          </Tabs>
        </Box>
      </div>

      {/* Profile Information */}
      <CustomTabPanel value={activeTab} index={0}>
        <div className="cards view_page">
          <div className="table_header mb_20">
            <h2>معلومات الملف الشخصي / Profile Information</h2>
            <span className={`status_btn dark ${!userData.isBlocked ? "c_success" : "c_warning"}`}>
              {!userData.isBlocked ? "نشط / Active" : "محظور / Blocked"}
            </span>
          </div>

          <div className="gap_p">
            <div className="vew_img w_100">
              <h6>الصورة / Image</h6>
              <figure>
                <img alt="user" src={userData.image || "/static/images/user_placeholder.png"} />
              </figure>
            </div>
            {profileInfo.map((item, index) => (
              <div className="w_33" key={index}>
                <h6>{item.label}</h6>
                <p>{item.value}</p>
              </div>
            ))}
          </div>
        </div>


        <div className="cards view_page">
          <div className="table_header mb_20">
            <h2>الباقة المحددة / Selected Plan</h2>
          </div>

          {userData.subscription?.planDetails ? (
            <div className="pricing__grid gap_m">
              <div className="pricing-card pricing-card--active" role="button" tabIndex={0}>
                <div className="pricing-card__header">
                  <span className="radio" />
                  <h3>{userData.subscription.planDetails.name || "N/A"}</h3>
                </div>

                {userData.subscription.planDetails.features?.length > 0 && (
                  <ul className="pricing-card__features">
                    {userData.subscription.planDetails.features.map((feature: string, index: number) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                )}

                <div className="pricing-card__price">
                  {userData.subscription.planDetails.amount !== undefined
                    ? `${userData.subscription.planDetails.currency?.toUpperCase() || ""} ${userData.subscription.planDetails.amount} / ${userData.subscription.planDetails.interval || ""}`
                    : "N/A"}
                </div>
              </div>
            </div>
          ) : (
            <p>لا توجد باقة نشطة / No active plan</p>
          )}
        </div>
      </CustomTabPanel>

      {/* Posted Vehicles */}
      <CustomTabPanel value={activeTab} index={1}>
        <div className="cards">
          <div className="table_header">
            <div className="left_s">
              <Box className="custom_tabs" sx={{ width: "100%" }}>
                <Tabs
                  value={valueTabs}
                  onChange={handleChangeTabs}
                  className="custom_tabs_links mb_0"
                >
                  {tabTypes.map((label, index) => (
                    <Tab key={label} label={label} {...a11yProps(index)} />
                  ))}
                </Tabs>
              </Box>
            </div>
          </div>
          {tabTypes.map((type, tabIndex) => (
            <CustomTabPanel key={type} value={valueTabs} index={tabIndex}>
              <VehicleListings 
                type={type} 
                showFeatured={false} 
                showDisableEnable={false} 
                showActions={false} 
                customData={userData.postedCars?.map((car: any) => ({ ...car, dealer: userData })) || []}
              />
            </CustomTabPanel>
          ))}
        </div>
      </CustomTabPanel>

      {/* Saved Vehicles */}
      <CustomTabPanel value={activeTab} index={2}>
        <div className="cards">
          <div className="table_header">
            <div className="left_s">
              <Box className="custom_tabs" sx={{ width: "100%" }}>
                <Tabs
                  value={valueTabs}
                  onChange={handleChangeTabs}
                  className="custom_tabs_links mb_0"
                >
                  {tabTypes.map((label, index) => (
                    <Tab key={label} label={label} {...a11yProps(index)} />
                  ))}
                </Tabs>
              </Box>
            </div>
          </div>
          {tabTypes.map((type, tabIndex) => (
            <CustomTabPanel key={type} value={valueTabs} index={tabIndex}>
              <VehicleListings 
                type={type} 
                showFeatured={false} 
                showDisableEnable={false}
                showDealerDetail={false} 
                showActions={false} 
                customData={userData.savedCars?.map((item: any) => item.carId).filter(Boolean) || []}
              />
            </CustomTabPanel>
          ))}
        </div>
      </CustomTabPanel>
    </>
  );
};

export default ManageUserDetail;